import * as React from 'react';
import { useState } from 'react';
import { DataGrid, GridToolbar, GridColDef } from '@mui/x-data-grid';
import { Box, TextField, MenuItem, Modal, Typography, IconButton } from '@mui/material';
import Grid from '@mui/material/Grid2';
// import { CSVLink } from 'react-csv';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { styled } from '@mui/system';

const StyledDataGrid = styled(DataGrid)({
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: '#e0f7fa',
	},
	'& .MuiDataGrid-row': {
		backgroundColor: 'transparent',
		'&:hover': {
			backgroundColor: '#e0f7fa',
		},
	},
	'& .MuiDataGrid-columnHeaderTitleContainer': {
		'&:hover': {
			color: '#000',
		},
	},
});

interface Order {
	id: number;
	orderId: string;
	email: string;
	orderedItems: string;
	date: string;
	orderStatus: string;
	item: string;
	totalPrice: number;
	paymentStatus: string;
}

const CustomerPage: React.FC = () => {
	const [searchText, setSearchText] = useState<string>('');
	const [filterStatus, setFilterStatus] = useState<string>('all');
	const [open, setOpen] = useState<boolean>(false);
	const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

	const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchText(event.target.value);
	};

	const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFilterStatus(event.target.value);
	};

	const handleViewDetails = (order: Order) => {
		setSelectedOrder(order);
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const columns: GridColDef[] = [
		{ field: 'orderId', headerName: 'Order ID', width: 150 },
		{ field: 'email', headerName: 'Email', width: 200 },
		{ field: 'orderedItems', headerName: 'Ordered Items', width: 150 },
		{ field: 'date', headerName: 'Date', width: 150 },
		{ field: 'orderStatus', headerName: 'Order Status', width: 150 },
		{ field: 'item', headerName: 'Item', width: 100 },
		{ field: 'totalPrice', headerName: 'Total Price', width: 150 },
		{ field: 'paymentStatus', headerName: 'Payment Status', width: 150 },
		{
			field: 'actions',
			headerName: 'Actions',
			width: 100,
			renderCell: (params) => (
				<IconButton onClick={() => handleViewDetails(params.row)}>
					<VisibilityIcon />
				</IconButton>
			),
		},
	];

	const rows: Order[] = [
		{
			id: 1,
			orderId: '59217',
			email: 'sarah@gmail.com',
			orderedItems: 'Pen stand',
			date: '14/07/2025',
			orderStatus: 'New order',
			item: '1',
			totalPrice: 5000,
			paymentStatus: 'Razor Pay',
		},
		// Add more rows as needed
	];

	const filteredRows = rows.filter(
		(row) =>
			row.orderId.includes(searchText) ||
			row.email.toLowerCase().includes(searchText.toLowerCase())
	);

	return (
		<Box sx={{ width: '100%' }}>
			<Grid container spacing={2} sx={{ mb: 2 }}>
				<Grid size={{ xs: 12, sm: 6, md: 10 }}>
					<TextField
						label="Search"
						variant="outlined"
						value={searchText}
						onChange={handleSearch}
						fullWidth
					/>
				</Grid>
				<Grid size={{ xs: 12, sm: 6, md: 2 }}>
					<TextField
						select
						label="Filter by Status"
						value={filterStatus}
						onChange={handleFilterChange}
						variant="outlined"
						fullWidth
						style={{ fontFamily: 'Poppins' }}
					>
						<MenuItem value="all" style={{ fontFamily: 'Poppins' }}>
							All
						</MenuItem>
						<MenuItem value="new" style={{ fontFamily: 'Poppins' }}>
							New Order
						</MenuItem>
						<MenuItem value="shipped" style={{ fontFamily: 'Poppins' }}>
							Shipped
						</MenuItem>
						<MenuItem value="cancelled" style={{ fontFamily: 'Poppins' }}>
							Cancelled
						</MenuItem>
					</TextField>
				</Grid>
				{/* <Grid size={{ xs: 12, sm: 6, md: 4 }}>
					<CSVLink data={filteredRows} filename="orders.csv">
						<Button variant="contained" color="primary" fullWidth>
							Export CSV
						</Button>
					</CSVLink>
				</Grid> */}
			</Grid>
			<Box sx={{ height: 600, width: '100%' }}>
				<StyledDataGrid
					rows={filteredRows}
					columns={columns}
					initialState={{
						pagination: {
							paginationModel: { pageSize: 10 },
						},
					}}
					pageSizeOptions={[10]}
					slots={{ toolbar: GridToolbar }}
				/>
			</Box>
			<Modal
				open={open}
				onClose={handleClose}
				aria-labelledby="modal-modal-title"
				aria-describedby="modal-modal-description"
			>
				<Box
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						width: 400,
						bgcolor: 'background.paper',
						border: '2px solid #000',
						boxShadow: 24,
						p: 4,
					}}
				>
					<Typography
						id="modal-modal-title"
						variant="h6"
						component="h2"
						style={{ fontFamily: 'Poppins' }}
					>
						Order Details
					</Typography>
					{selectedOrder && (
						<Typography id="modal-modal-description" sx={{ mt: 2 }}>
							<p style={{ fontFamily: 'Poppins' }}>
								<strong style={{ fontFamily: 'Poppins' }}>Order ID:</strong>{' '}
								{selectedOrder.orderId}
							</p>
							<p style={{ fontFamily: 'Poppins' }}>
								<strong style={{ fontFamily: 'Poppins' }}>Email:</strong>{' '}
								{selectedOrder.email}
							</p>
							<p style={{ fontFamily: 'Poppins' }}>
								<strong style={{ fontFamily: 'Poppins' }}>Ordered Items:</strong>{' '}
								{selectedOrder.orderedItems}
							</p>
							<p style={{ fontFamily: 'Poppins' }}>
								<strong style={{ fontFamily: 'Poppins' }}>Date:</strong>{' '}
								{selectedOrder.date}
							</p>
							<p style={{ fontFamily: 'Poppins' }}>
								<strong style={{ fontFamily: 'Poppins' }}>Order Status:</strong>{' '}
								{selectedOrder.orderStatus}
							</p>
							<p style={{ fontFamily: 'Poppins' }}>
								<strong style={{ fontFamily: 'Poppins' }}>Item:</strong>{' '}
								{selectedOrder.item}
							</p>
							<p style={{ fontFamily: 'Poppins' }}>
								<strong style={{ fontFamily: 'Poppins' }}>Total Price:</strong>{' '}
								{selectedOrder.totalPrice}
							</p>
							<p style={{ fontFamily: 'Poppins' }}>
								<strong style={{ fontFamily: 'Poppins' }}>Payment Status:</strong>{' '}
								{selectedOrder.paymentStatus}
							</p>
						</Typography>
					)}
				</Box>
			</Modal>
		</Box>
	);
};

export default CustomerPage;
